import { useState, useEffect, useRef, useCallback } from "react";
import { getApiBaseUrl, getAuthHeaders } from "@/lib/api";

interface JobGroup {
  worker: string;
  status: string;
  count: number;
  latestAt: string;
}

interface JobSummary {
  pending: number;
  running: number;
  completed: number;
  failed: number;
}

interface QueueDepthEntry {
  queue: string;
  pending: number;
  running: number;
  scheduled: number;
}

interface LongRunningJob {
  id: number;
  worker: string;
  queue: string;
  startedAt: string;
  attempts: number;
  maxAttempts: number;
  leaseExpiresAt: string | null;
}

interface ApiEndpoint {
  method: string;
  path: string;
  total: number;
  "2xx": number;
  "4xx": number;
  "5xx": number;
  errorRate: number;
}

interface ApiTotals {
  total: number;
  "2xx": number;
  "4xx": number;
  "5xx": number;
  errorRate: number;
}

interface ErrorPattern {
  context: string;
  pattern: string;
  count: number;
  latestAt: string;
  firstAt: string;
}

interface FailedJob {
  id: number;
  worker: string;
  queue: string;
  error: string | null;
  attempts: number;
  maxAttempts: number;
  failedAt: string;
}

interface ProcessStats {
  uptimeSeconds: number;
  memoryMb: {
    rss: number;
    heapUsed: number;
    heapTotal: number;
    external: number;
  };
  cpuUsage: {
    user: number;
    system: number;
  };
  system: {
    totalMemoryMb: number;
    freeMemoryMb: number;
    memoryUsagePercent: number;
    cpuCount: number;
    loadAvg: [number, number, number];
    platform: string;
  };
}

export interface LiveState {
  connected: boolean;
  jobActivity: JobGroup[];
  jobSummary: JobSummary | null;
  queueDepth: QueueDepthEntry[];
  longRunningJobs: LongRunningJob[];
  recentFailures: FailedJob[];
  apiMetrics: ApiEndpoint[];
  apiTotals: ApiTotals | null;
  errorPatterns: ErrorPattern[];
  processStats: ProcessStats | null;
  fadingJobs: Map<string, number>; // key -> fadeAt timestamp
}

export function useSystemLive() {
  const [state, setState] = useState<LiveState>({
    connected: false,
    jobActivity: [],
    jobSummary: null,
    queueDepth: [],
    longRunningJobs: [],
    recentFailures: [],
    apiMetrics: [],
    apiTotals: null,
    errorPatterns: [],
    processStats: null,
    fadingJobs: new Map(),
  });

  const abortRef = useRef<AbortController | null>(null);
  const retryCountRef = useRef(0);
  const retryTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const fadeTimerRef = useRef<ReturnType<typeof setInterval>>();

  const processEvent = useCallback((event: string, data: any) => {
    setState((prev) => {
      switch (event) {
        case "init": {
          return {
            ...prev,
            connected: true,
            jobActivity: data.jobActivity?.groups ?? [],
            jobSummary: data.jobActivity?.summary ?? null,
            queueDepth: data.queueDepth ?? [],
            longRunningJobs: data.longRunningJobs ?? [],
            recentFailures: data.recentFailures ?? [],
            apiMetrics: data.apiMetrics?.endpoints ?? [],
            apiTotals: data.apiMetrics?.totals ?? null,
            errorPatterns: data.errorPatterns ?? [],
            processStats: data.processStats ?? null,
          };
        }
        case "job-activity": {
          const newGroups: JobGroup[] = data.groups ?? [];
          const summary: JobSummary = data.summary;

          // Split server data into active (pending/running) and terminal (completed/failed)
          const activeFromServer = newGroups.filter(
            (g) => g.status === "pending" || g.status === "running"
          );
          const terminalFromServer = newGroups.filter(
            (g) => g.status === "completed" || g.status === "failed"
          );

          // For terminal entries: keep still-fading ones from prev, merge new ones on top
          const terminalMap = new Map<string, JobGroup>();
          for (const g of prev.jobActivity) {
            if (g.status !== "completed" && g.status !== "failed") continue;
            const key = `${g.worker}-${g.status}`;
            if (prev.fadingJobs.has(key)) terminalMap.set(key, g);
          }
          for (const g of terminalFromServer) {
            terminalMap.set(`${g.worker}-${g.status}`, g);
          }

          // Track fade timers for new terminal entries
          const newFading = new Map(prev.fadingJobs);
          for (const g of terminalFromServer) {
            const key = `${g.worker}-${g.status}`;
            if (!newFading.has(key)) {
              newFading.set(key, Date.now() + 3000);
            }
          }

          return {
            ...prev,
            // Active jobs: replace entirely with server data (fixes stale entries)
            // Terminal jobs: merge with fade-out
            jobActivity: [...activeFromServer, ...terminalMap.values()],
            jobSummary: summary ?? prev.jobSummary,
            queueDepth: data.queueDepth ?? prev.queueDepth,
            longRunningJobs: data.longRunningJobs ?? prev.longRunningJobs,
            fadingJobs: newFading,
          };
        }
        case "api-metrics": {
          return {
            ...prev,
            apiMetrics: data.endpoints ?? [],
            apiTotals: data.totals ?? null,
          };
        }
        case "error-patterns": {
          return {
            ...prev,
            errorPatterns: data.patterns ?? [],
            recentFailures: data.recentFailures ?? prev.recentFailures,
          };
        }
        case "process-stats": {
          return {
            ...prev,
            processStats: data,
          };
        }
        default:
          return prev;
      }
    });
  }, []);

  const connect = useCallback(() => {
    const abort = new AbortController();
    abortRef.current = abort;

    const url = `${getApiBaseUrl()}/api/system/live`;
    const headers = getAuthHeaders();

    fetch(url, { headers, signal: abort.signal })
      .then(async (response) => {
        if (!response.ok || !response.body) {
          throw new Error(`SSE failed: ${response.status}`);
        }

        retryCountRef.current = 0;
        setState((prev) => ({ ...prev, connected: true }));

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = "";

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });

          // Parse SSE frames
          const frames = buffer.split("\n\n");
          buffer = frames.pop() ?? "";

          for (const frame of frames) {
            if (!frame.trim() || frame.startsWith(":")) continue;

            let eventType = "message";
            let eventData = "";

            for (const rawLine of frame.split("\n")) {
              const line = rawLine.endsWith("\r") ? rawLine.slice(0, -1) : rawLine;
              if (line.startsWith("event: ")) {
                eventType = line.slice(7);
              } else if (line.startsWith("data: ")) {
                if (eventData) eventData += "\n";
                eventData += line.slice(6);
              }
            }

            if (eventData) {
              try {
                processEvent(eventType, JSON.parse(eventData));
              } catch {
                // skip malformed data
              }
            }
          }
        }

        // Stream ended normally
        setState((prev) => ({ ...prev, connected: false }));
        scheduleReconnect();
      })
      .catch((err) => {
        if (abort.signal.aborted) return;
        setState((prev) => ({ ...prev, connected: false }));
        scheduleReconnect();
      });

    function scheduleReconnect() {
      const delay = Math.min(3000 * Math.pow(2, retryCountRef.current), 30000);
      retryCountRef.current++;
      retryTimerRef.current = setTimeout(connect, delay);
    }
  }, [processEvent]);

  useEffect(() => {
    connect();

    // Fade-out pruning interval
    fadeTimerRef.current = setInterval(() => {
      setState((prev) => {
        const now = Date.now();
        let changed = false;
        const newFading = new Map(prev.fadingJobs);
        const expiredKeys = new Set<string>();
        for (const [key, fadeAt] of newFading) {
          if (fadeAt < now) {
            newFading.delete(key);
            expiredKeys.add(key);
            changed = true;
          }
        }
        if (!changed) return prev;
        // Remove fully faded job groups from activity list
        const newActivity = prev.jobActivity.filter(
          (g) => !expiredKeys.has(`${g.worker}-${g.status}`)
        );
        return { ...prev, fadingJobs: newFading, jobActivity: newActivity };
      });
    }, 500);

    return () => {
      abortRef.current?.abort();
      if (retryTimerRef.current) clearTimeout(retryTimerRef.current);
      if (fadeTimerRef.current) clearInterval(fadeTimerRef.current);
    };
  }, [connect]);

  return state;
}
